import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Building2, CalendarDays, Plus, Pencil, Trash2, X, Flag, Coins, Tag, Palmtree, Inbox } from 'lucide-react'
import { getLeavePolicy, setLeavePolicy } from '@/api/analytics'
import { getLeaveTypes, setLeaveTypes, type LeaveTypeConfig } from '@/api/leaves'
import { getOrganization, updateOrganization } from '@/api/organizations'
import { getRequestTypes, setDisabledRequestTypes } from '@/api/requests'
import {
  listDepartments, createDepartment, updateDepartment, deleteDepartment,
  listDesignations, createDesignation, updateDesignation, deleteDesignation,
  type Department, type Designation,
} from '@/api/orgstructure'
import {
  listHolidays, createHoliday, deleteHoliday, seedPakistanHolidays, type Holiday,
} from '@/api/holidays'
import { listUsers } from '@/api/users'
import { cn } from '@/lib/utils'

type Tab = 'designations' | 'holidays' | 'leave-types' | 'leave-policy' | 'request-types'
const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EC5B2C]/40'
const CURRENT_YEAR = new Date().getFullYear()

export default function Organization() {
  const [tab, setTab] = useState<Tab>('designations')
  const tabs: { key: Tab; label: string; icon: any }[] = [
    { key: 'designations', label: 'Designations', icon: Flag },
    { key: 'holidays', label: 'Holidays', icon: CalendarDays },
    { key: 'leave-types', label: 'Leave Types', icon: Palmtree },
    { key: 'leave-policy', label: 'Leave Policy', icon: Coins },
    { key: 'request-types', label: 'Request Types', icon: Inbox },
  ]
  return (
    <div className="max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Organization</h1>
      <p className="text-sm text-gray-500 mb-6">Departments, job titles, and the holiday calendar.</p>

      <div className="flex gap-1 border-b border-gray-200 mb-5 overflow-x-auto no-scrollbar">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex shrink-0 items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${
              tab === t.key ? 'border-[#EC5B2C] text-[#EC5B2C]' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <t.icon className="h-4 w-4" /> {t.label}
          </button>
        ))}
      </div>

      {tab === 'designations' && <DesignationsTab />}
      {tab === 'holidays' && <HolidaysTab />}
      {tab === 'leave-types' && <LeaveTypesTab />}
      {tab === 'leave-policy' && <LeavePolicyTab />}
      {tab === 'request-types' && <RequestTypesTab />}
    </div>
  )
}

// ── Assets (inventory asset-ID prefix) ───────────────────────────────────────
function AssetsTab() {
  const qc = useQueryClient()
  const { data: org } = useQuery({ queryKey: ['organization'], queryFn: getOrganization })
  const [prefix, setPrefix] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => { if (org) setPrefix(org.asset_prefix ?? '') }, [org])

  const mut = useMutation({
    mutationFn: () => updateOrganization({ asset_prefix: prefix.trim() || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['organization'] }); setSaved(true); setTimeout(() => setSaved(false), 2000) },
  })

  const derived = (org?.name || 'ORG').replace(/[^a-zA-Z0-9]/g, '').slice(0, 4).toUpperCase() || 'ORG'
  const effective = (prefix.trim().replace(/[^a-zA-Z0-9]/g, '').toUpperCase() || derived).slice(0, 16)

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 max-w-xl">
      {saved && <div className="mb-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2">Saved.</div>}
      <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2"><Tag className="h-4 w-4 text-[#EC5B2C]" /> Asset ID prefix</h2>
      <p className="text-xs text-gray-400 mb-3">
        Used for auto-generated inventory asset IDs, e.g. <span className="font-mono">{effective}-LAP-0001</span>.
        Leave blank to use a code derived from your organization name.
      </p>
      <Field label="Prefix">
        <input
          className={`${inputCls} font-mono uppercase`}
          value={prefix}
          maxLength={16}
          placeholder={derived}
          onChange={(e) => setPrefix(e.target.value)}
        />
      </Field>
      <button onClick={() => mut.mutate()} disabled={mut.isPending} className="mt-1 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">Save</button>
    </div>
  )
}

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b px-5 py-3">
          <div className="font-semibold text-gray-900">{title}</div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block mb-3">
      <span className="block text-xs font-medium text-gray-600 mb-1">{label}</span>
      {children}
    </label>
  )
}

// ── Departments ──────────────────────────────────────────────────────────────
function DepartmentsTab() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Department | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<{ name: string; code: string; description: string; head_user_id: string }>({ name: '', code: '', description: '', head_user_id: '' })
  const [error, setError] = useState('')

  const { data: depts = [] } = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const { data: usersData } = useQuery({ queryKey: ['users', 'active-all'], queryFn: () => listUsers({ is_active: true, per_page: 100 }) })
  const users = usersData?.items ?? []
  const invalidate = () => qc.invalidateQueries({ queryKey: ['departments'] })

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = { name: form.name, code: form.code || null, description: form.description || null, head_user_id: form.head_user_id || null }
      return editing ? updateDepartment(editing.id, payload) : createDepartment(payload)
    },
    onSuccess: () => { invalidate(); close() },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save'),
  })
  const delMut = useMutation({ mutationFn: (id: string) => deleteDepartment(id), onSuccess: invalidate })

  function open(d?: Department) {
    setEditing(d || null)
    setForm({ name: d?.name || '', code: d?.code || '', description: d?.description || '', head_user_id: d?.head_user_id || '' })
    setError(''); setShowForm(true)
  }
  function close() { setShowForm(false); setEditing(null); setError('') }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => open()} className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-3 py-1.5 text-sm font-medium text-white"><Plus className="h-4 w-4" /> Add Department</button>
      </div>
      <Table
        head={['Name', 'Code', 'Head', 'Members', 'Status', '']}
        rows={depts.map((d) => [
          <span className="font-medium text-gray-900">{d.name}</span>,
          d.code || '—', d.head_user_name || '—', String(d.member_count),
          <StatusBadge active={d.is_active} />,
          <RowActions onEdit={() => open(d)} onDelete={() => { if (confirm(`Deactivate "${d.name}"?`)) delMut.mutate(d.id) }} />,
        ])}
        empty="No departments yet."
      />
      {showForm && (
        <Modal title={editing ? 'Edit Department' : 'Add Department'} onClose={close}>
          {error && <div className="mb-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
          <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Code"><input className={inputCls} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></Field>
          <Field label="Department Head">
            <select className={inputCls} value={form.head_user_id} onChange={(e) => setForm({ ...form, head_user_id: e.target.value })}>
              <option value="">—</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>
          <Field label="Description"><textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <FormButtons onCancel={close} onSave={() => saveMut.mutate()} saving={saveMut.isPending} disabled={!form.name} />
        </Modal>
      )}
    </div>
  )
}

// ── Designations ─────────────────────────────────────────────────────────────
function DesignationsTab() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<Designation | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<{ title: string; level: string; description: string }>({ title: '', level: '', description: '' })
  const [error, setError] = useState('')

  const { data: desigs = [] } = useQuery({ queryKey: ['designations'], queryFn: () => listDesignations() })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['designations'] })

  const saveMut = useMutation({
    mutationFn: () => {
      const payload = { title: form.title, level: form.level || null, description: form.description || null }
      return editing ? updateDesignation(editing.id, payload) : createDesignation(payload)
    },
    onSuccess: () => { invalidate(); close() },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save'),
  })
  const delMut = useMutation({ mutationFn: (id: string) => deleteDesignation(id), onSuccess: invalidate })

  function open(d?: Designation) {
    setEditing(d || null)
    setForm({ title: d?.title || '', level: d?.level || '', description: d?.description || '' })
    setError(''); setShowForm(true)
  }
  function close() { setShowForm(false); setEditing(null); setError('') }

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => open()} className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-3 py-1.5 text-sm font-medium text-white"><Plus className="h-4 w-4" /> Add Designation</button>
      </div>
      <Table
        head={['Title', 'Level', 'Members', 'Status', '']}
        rows={desigs.map((d) => [
          <span className="font-medium text-gray-900">{d.title}</span>,
          d.level || '—', String(d.member_count),
          <StatusBadge active={d.is_active} />,
          <RowActions onEdit={() => open(d)} onDelete={() => { if (confirm(`Deactivate "${d.title}"?`)) delMut.mutate(d.id) }} />,
        ])}
        empty="No designations yet."
      />
      {showForm && (
        <Modal title={editing ? 'Edit Designation' : 'Add Designation'} onClose={close}>
          {error && <div className="mb-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
          <Field label="Title"><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
          <Field label="Level"><input className={inputCls} placeholder="junior / mid / senior / lead" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} /></Field>
          <Field label="Description"><textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <FormButtons onCancel={close} onSave={() => saveMut.mutate()} saving={saveMut.isPending} disabled={!form.title} />
        </Modal>
      )}
    </div>
  )
}

// ── Holidays ─────────────────────────────────────────────────────────────────
function HolidaysTab() {
  const qc = useQueryClient()
  const [year, setYear] = useState(CURRENT_YEAR)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<{ date: string; name: string; is_recurring: boolean }>({ date: '', name: '', is_recurring: false })
  const [error, setError] = useState('')

  const { data: holidays = [] } = useQuery({ queryKey: ['holidays', year], queryFn: () => listHolidays(year) })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['holidays'] })

  const createMut = useMutation({
    mutationFn: () => createHoliday({ date: form.date, name: form.name, is_recurring: form.is_recurring, country: 'PK' }),
    onSuccess: () => { invalidate(); setShowForm(false); setForm({ date: '', name: '', is_recurring: false }) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save'),
  })
  const delMut = useMutation({ mutationFn: (id: string) => deleteHoliday(id), onSuccess: invalidate })
  const seedMut = useMutation({ mutationFn: () => seedPakistanHolidays(year), onSuccess: invalidate })

  const years = [CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1]

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <select className={`${inputCls} w-auto`} value={year} onChange={(e) => setYear(Number(e.target.value))}>
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={() => seedMut.mutate()} disabled={seedMut.isPending} className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 disabled:opacity-60">
            <Flag className="h-4 w-4" /> Add Pakistan holidays {year}
          </button>
          <button onClick={() => { setError(''); setShowForm(true) }} className="flex items-center gap-2 rounded-lg bg-[#EC5B2C] px-3 py-1.5 text-sm font-medium text-white"><Plus className="h-4 w-4" /> Add Holiday</button>
        </div>
      </div>
      <Table
        head={['Date', 'Name', 'Recurring', '']}
        rows={holidays.map((h: Holiday) => [
          new Date(h.date + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }),
          <span className="font-medium text-gray-900">{h.name}</span>,
          h.is_recurring ? 'Yes' : 'No',
          <RowActions onDelete={() => { if (confirm(`Delete "${h.name}"?`)) delMut.mutate(h.id) }} />,
        ])}
        empty="No holidays for this year yet."
      />
      {showForm && (
        <Modal title="Add Holiday" onClose={() => setShowForm(false)}>
          {error && <div className="mb-3 rounded bg-red-50 border border-red-200 text-red-600 text-sm px-3 py-2">{error}</div>}
          <Field label="Date"><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></Field>
          <Field label="Name"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <label className="flex items-center gap-2 mb-3 text-sm text-gray-700">
            <input type="checkbox" checked={form.is_recurring} onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })} /> Recurring every year
          </label>
          <FormButtons onCancel={() => setShowForm(false)} onSave={() => createMut.mutate()} saving={createMut.isPending} disabled={!form.date || !form.name} />
        </Modal>
      )}
    </div>
  )
}

// ── Shared bits ──────────────────────────────────────────────────────────────
function Table({ head, rows, empty }: { head: string[]; rows: React.ReactNode[][]; empty: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
      <table className="w-full text-sm min-w-[560px]">
        <thead className="bg-gray-50 text-gray-500 text-left">
          <tr>{head.map((h, i) => <th key={i} className="px-4 py-3 font-medium">{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} className="px-4 py-8 text-center text-gray-400">{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">{r.map((c, j) => <td key={j} className="px-4 py-3 text-gray-600">{c}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function StatusBadge({ active }: { active: boolean }) {
  return <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>{active ? 'Active' : 'Inactive'}</span>
}

function RowActions({ onEdit, onDelete }: { onEdit?: () => void; onDelete: () => void }) {
  return (
    <div className="flex items-center justify-end gap-1">
      {onEdit && <button title="Edit" onClick={onEdit} className="p-1.5 rounded hover:bg-gray-100 text-gray-500"><Pencil className="h-4 w-4" /></button>}
      <button title="Delete" onClick={onDelete} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>
    </div>
  )
}

function FormButtons({ onCancel, onSave, saving, disabled }: { onCancel: () => void; onSave: () => void; saving: boolean; disabled: boolean }) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button onClick={onCancel} className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600">Cancel</button>
      <button onClick={onSave} disabled={saving || disabled} className="px-4 py-2 text-sm rounded-lg bg-[#EC5B2C] text-white disabled:opacity-60">Save</button>
    </div>
  )
}

// ── Leave Types (org-configurable) ───────────────────────────────────────────
const TYPE_COLORS = ['#eab308', '#f97316', '#dc2626', '#0891b2', '#16a34a', '#7c3aed', '#64748b', '#0ea5e9']
const slugify = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 20)

const GENDER_OPTS = ['male', 'female', 'other']
const EMPTYPE_OPTS = ['full_time', 'part_time', 'contract', 'intern']
const MARITAL_OPTS = ['single', 'married', 'divorced', 'widowed']
const ROLE_OPTS = ['team_member', 'project_lead', 'manager', 'admin', 'accounts', 'hr']

type Cond = {
  genders?: string[]; employment_types?: string[]; marital_statuses?: string[]
  departments?: string[]; designations?: string[]; roles?: string[]; min_tenure_months?: number
}

function Chips({ label, opts, sel = [], onToggle, fmt }: {
  label: string; opts: { v: string; l: string }[]; sel?: string[]; onToggle: (v: string) => void; fmt?: (v: string) => string
}) {
  return (
    <div>
      <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</div>
      <div className="flex flex-wrap gap-1">
        {opts.map((o) => {
          const on = sel.includes(o.v)
          return (
            <button key={o.v} type="button" onClick={() => onToggle(o.v)}
              className={cn('rounded-full border px-2 py-0.5 text-xs capitalize transition',
                on ? 'border-[#EC5B2C] bg-[#EC5B2C]/10 text-[#EC5B2C]' : 'border-gray-200 text-gray-500 hover:bg-gray-50')}>
              {fmt ? fmt(o.l) : o.l}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ConditionEditor({ value, onChange, departments, designations }: {
  value: Cond; onChange: (c: Cond) => void; departments: Department[]; designations: Designation[]
}) {
  const toggle = (key: keyof Cond, v: string) => {
    const cur = (value[key] as string[] | undefined) || []
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]
    onChange({ ...value, [key]: next.length ? next : undefined })
  }
  const label = (s: string) => s.replace('_', ' ')
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Chips label="Gender" opts={GENDER_OPTS.map((v) => ({ v, l: v }))} sel={value.genders} onToggle={(v) => toggle('genders', v)} />
      <Chips label="Employment type" opts={EMPTYPE_OPTS.map((v) => ({ v, l: label(v) }))} sel={value.employment_types} onToggle={(v) => toggle('employment_types', v)} />
      <Chips label="Marital status" opts={MARITAL_OPTS.map((v) => ({ v, l: v }))} sel={value.marital_statuses} onToggle={(v) => toggle('marital_statuses', v)} />
      <Chips label="Role" opts={ROLE_OPTS.map((v) => ({ v, l: label(v) }))} sel={value.roles} onToggle={(v) => toggle('roles', v)} />
      {departments.length > 0 && <Chips label="Department" opts={departments.map((d) => ({ v: d.id, l: d.name }))} sel={value.departments} onToggle={(v) => toggle('departments', v)} />}
      {designations.length > 0 && <Chips label="Designation" opts={designations.map((d) => ({ v: d.id, l: d.title }))} sel={value.designations} onToggle={(v) => toggle('designations', v)} />}
      <label className="block">
        <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-400">Min tenure (months)</span>
        <input type="number" min={0} className={`${inputCls} w-28`} value={value.min_tenure_months ?? ''} onChange={(e) => onChange({ ...value, min_tenure_months: e.target.value ? Number(e.target.value) : undefined })} />
      </label>
    </div>
  )
}

function LeaveTypesTab() {
  const qc = useQueryClient()
  const { data: types } = useQuery({ queryKey: ['leave-types'], queryFn: getLeaveTypes })
  const { data: departments = [] } = useQuery({ queryKey: ['departments'], queryFn: () => listDepartments() })
  const { data: designations = [] } = useQuery({ queryKey: ['designations'], queryFn: () => listDesignations() })
  const [rows, setRows] = useState<LeaveTypeConfig[]>([])
  const [expanded, setExpanded] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)

  useEffect(() => { if (types) setRows(types) }, [types])

  const mut = useMutation({
    mutationFn: () => setLeaveTypes(rows.map((r) => ({ ...r, key: r.key || slugify(r.label) }))),
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ['leave-types'] }); setRows(d); setSaved(true); setError(''); setTimeout(() => setSaved(false), 2000) },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Failed to save'),
  })

  const update = (i: number, patch: Partial<LeaveTypeConfig>) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  const remove = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i))
  const add = () => setRows((rs) => [...rs, { key: '', label: '', paid: true, requires_approval: true, default_quota: 0, color: TYPE_COLORS[rs.length % TYPE_COLORS.length] }])

  const rulesSummary = (r: LeaveTypeConfig) => {
    const bits: string[] = []
    const e = r.eligibility || {}
    if (Object.keys(e).length) bits.push('eligibility')
    if (r.quota_rules?.length) bits.push(`${r.quota_rules.length} quota rule${r.quota_rules.length > 1 ? 's' : ''}`)
    if (r.min_days != null || r.max_days != null || r.min_notice_days != null) bits.push('limits')
    return bits.length ? bits.join(' · ') : 'no rules'
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      <div className="mb-1 flex items-center justify-between">
        <h2 className="font-semibold text-gray-900">Leave Types</h2>
        <button onClick={add} className="flex items-center gap-1 text-sm text-[#EC5B2C] hover:underline"><Plus className="h-4 w-4" /> Add type</button>
      </div>
      <p className="mb-4 text-xs text-gray-500">Define leave types employees can request. Expand a type to set eligibility (who can request it), conditional quotas, and per-request limits.</p>

      {error && <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>}
      {saved && <div className="mb-3 rounded bg-green-50 px-3 py-2 text-sm text-green-700">Saved.</div>}

      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="rounded-lg border border-gray-100">
            <div className="grid grid-cols-2 items-center gap-2 p-2 sm:grid-cols-[1.4fr_0.6fr_0.7fr_1.1fr_auto_auto_auto]">
              <input className={inputCls} placeholder="e.g. Bereavement" value={r.label} onChange={(e) => update(i, { label: e.target.value, key: r.key || slugify(e.target.value) })} />
              <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={r.paid} onChange={(e) => update(i, { paid: e.target.checked })} /> Paid</label>
              <label className="flex items-center gap-1.5 text-sm text-gray-600"><input type="checkbox" checked={r.requires_approval} onChange={(e) => update(i, { requires_approval: e.target.checked })} /> Approval</label>
              <div className="flex items-center gap-1">
                <input type="number" min={0} step={0.5} title={r.quota_period === 'month' ? 'Default quota per month' : 'Default annual quota'} className={`${inputCls} min-w-[3.5rem]`} value={r.default_quota} onChange={(e) => update(i, { default_quota: Number(e.target.value) })} />
                <select
                  title="Quota period — per month resets every month (e.g. 2 WFH a month)"
                  className="rounded-md border border-gray-200 px-1 py-2 text-xs text-gray-600"
                  value={r.quota_period === 'month' ? 'month' : 'year'}
                  onChange={(e) => update(i, { quota_period: e.target.value as 'year' | 'month' })}
                >
                  <option value="year">/ year</option>
                  <option value="month">/ month</option>
                </select>
              </div>
              <input type="color" className="h-9 w-10 cursor-pointer rounded border border-gray-200" value={r.color} onChange={(e) => update(i, { color: e.target.value })} />
              <button onClick={() => setExpanded(expanded === i ? null : i)} className="rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 whitespace-nowrap">
                {expanded === i ? 'Hide' : 'Rules'} <span className="text-gray-400">({rulesSummary(r)})</span>
              </button>
              <button onClick={() => remove(i)} className="justify-self-end p-1.5 text-gray-300 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>

            {expanded === i && (
              <div className="space-y-5 border-t border-gray-100 bg-gray-50/50 p-4">
                {/* Eligibility */}
                <div>
                  <h4 className="mb-2 text-xs font-semibold text-gray-700">Eligibility <span className="font-normal text-gray-400">— who can request this (leave blank = everyone)</span></h4>
                  <ConditionEditor value={r.eligibility || {}} onChange={(c) => update(i, { eligibility: c })} departments={departments} designations={designations} />
                </div>

                {/* Quota rules */}
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-gray-700">Conditional quotas <span className="font-normal text-gray-400">— first match wins, else default {r.default_quota}/{r.quota_period === 'month' ? 'mo' : 'yr'}</span></h4>
                    <button onClick={() => update(i, { quota_rules: [...(r.quota_rules || []), { when: {}, quota: r.default_quota }] })} className="text-xs text-[#EC5B2C] hover:underline">+ Add rule</button>
                  </div>
                  <div className="space-y-3">
                    {(r.quota_rules || []).map((rule, ri) => (
                      <div key={ri} className="rounded-lg border border-gray-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between">
                          <span className="text-xs font-medium text-gray-600">If employee matches:</span>
                          <div className="flex items-center gap-2">
                            <label className="flex items-center gap-1 text-xs text-gray-600">quota <input type="number" min={0} step={0.5} className={`${inputCls} w-20`} value={rule.quota} onChange={(e) => update(i, { quota_rules: (r.quota_rules || []).map((x, xi) => xi === ri ? { ...x, quota: Number(e.target.value) } : x) })} /></label>
                            <button onClick={() => update(i, { quota_rules: (r.quota_rules || []).filter((_, xi) => xi !== ri) })} className="p-1 text-gray-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </div>
                        <ConditionEditor value={rule.when || {}} onChange={(c) => update(i, { quota_rules: (r.quota_rules || []).map((x, xi) => xi === ri ? { ...x, when: c } : x) })} departments={departments} designations={designations} />
                      </div>
                    ))}
                    {!(r.quota_rules?.length) && <p className="text-xs text-gray-400">No conditional quotas — everyone eligible gets {r.default_quota}/{r.quota_period === 'month' ? 'mo' : 'yr'}.</p>}
                  </div>
                </div>

                {/* Per-request limits */}
                <div>
                  <h4 className="mb-2 text-xs font-semibold text-gray-700">Per-request limits</h4>
                  <div className="flex flex-wrap gap-3">
                    <label className="text-xs text-gray-600">Min days<input type="number" min={0} step={0.5} className={`${inputCls} mt-1 w-24`} value={r.min_days ?? ''} onChange={(e) => update(i, { min_days: e.target.value ? Number(e.target.value) : null })} /></label>
                    <label className="text-xs text-gray-600">Max days<input type="number" min={0} step={0.5} className={`${inputCls} mt-1 w-24`} value={r.max_days ?? ''} onChange={(e) => update(i, { max_days: e.target.value ? Number(e.target.value) : null })} /></label>
                    <label className="text-xs text-gray-600">Advance notice (days)<input type="number" min={0} className={`${inputCls} mt-1 w-24`} value={r.min_notice_days ?? ''} onChange={(e) => update(i, { min_notice_days: e.target.value ? Number(e.target.value) : null })} /></label>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="py-4 text-center text-sm text-gray-400">No leave types. Add one to get started.</p>}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <button onClick={() => mut.mutate()} disabled={mut.isPending || rows.length === 0} className="rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">Save Leave Types</button>
        {types && <button onClick={() => setRows(types)} className="text-sm text-gray-500 hover:text-gray-700">Reset</button>}
      </div>
    </div>
  )
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

function fiscalInfo(startMonth: number) {
  const now = new Date()
  const cy = now.getFullYear()
  const startYear = now.getMonth() + 1 >= startMonth ? cy : cy - 1
  const start = new Date(startYear, startMonth - 1, 1)
  const end = new Date(startYear + 1, startMonth - 1, 1)
  end.setDate(end.getDate() - 1) // day before next fiscal year
  const reset = new Date(startYear, startMonth - 1, 1)
  reset.setDate(0) // last day of the previous month
  const fmt = (d: Date) => d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  const resetShort = reset.toLocaleDateString(undefined, { day: 'numeric', month: 'long' })
  return { windowLabel: `${fmt(start)} – ${fmt(end)}`, resetLabel: resetShort }
}

function LeavePolicyTab() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['leave-policy'], queryFn: getLeavePolicy })
  const [fiscalMonth, setFiscalMonth] = useState(7)
  const [backdateDays, setBackdateDays] = useState(90)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    if (data) {
      setFiscalMonth(data.fiscal_start_month || 7)
      setBackdateDays(data.backdate_days ?? 90)
    }
  }, [data])

  const mut = useMutation({
    // Only the fiscal-year setting lives here now; quotas are set under Leave Types.
    mutationFn: () => setLeavePolicy({
      leave_accrual: { enabled: false }, fiscal_start_month: fiscalMonth,
      backdate_days: Math.max(0, Math.min(365, Math.round(backdateDays || 0))),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['leave-policy'] }); qc.invalidateQueries({ queryKey: ['leave'] }); setSaved(true); setTimeout(() => setSaved(false), 2000) },
  })

  const info = fiscalInfo(fiscalMonth)

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5 max-w-xl">
      {saved && <div className="mb-3 rounded bg-green-50 border border-green-200 text-green-700 text-sm px-3 py-2">Saved. Balances now count within the fiscal year.</div>}

      {/* Fiscal year */}
      <h2 className="font-semibold text-gray-900 mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#EC5B2C]" /> Fiscal year</h2>
      <p className="text-xs text-gray-400 mb-3">Leave quotas are per fiscal year and reset automatically.</p>
      <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        Fiscal year starts in
        <select value={fiscalMonth} onChange={(e) => setFiscalMonth(Number(e.target.value))} className={`${inputCls} w-auto`}>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
        </select>
      </label>
      <p className="mt-2 text-xs text-gray-500">
        Quotas reset on <span className="font-medium text-gray-700">{info.resetLabel}</span>. Current fiscal year: <span className="font-medium text-gray-700">{info.windowLabel}</span>.
      </p>

      {/* Backdated leave */}
      <h2 className="font-semibold text-gray-900 mt-6 mb-1 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#EC5B2C]" /> Backdated leave</h2>
      <p className="text-xs text-gray-400 mb-3">Lets employees apply for days they missed. The request still goes to their approver.</p>
      <label className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
        Allow applying up to
        <input type="number" min={0} max={365} value={backdateDays} onChange={(e) => setBackdateDays(Number(e.target.value))} className={`${inputCls} w-24`} />
        days back
      </label>
      <p className="mt-2 text-xs text-gray-500">Set to 0 to allow only today and future dates.</p>

      <p className="mt-6 rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500">
        Annual quota per leave type is set under the <span className="font-medium text-gray-700">Leave Types</span> tab; per-employee overrides under <span className="font-medium text-gray-700">Users → Leave Quotas</span>.
      </p>

      <button onClick={() => mut.mutate()} disabled={mut.isPending} className="mt-4 rounded-lg bg-[#EC5B2C] px-4 py-2 text-sm font-medium text-white disabled:opacity-60">Save</button>
    </div>
  )
}


// ── Request Types (which kinds employees can raise on the Requests page) ─────
function RequestTypesTab() {
  const qc = useQueryClient()
  const { data } = useQuery({ queryKey: ['request-types'], queryFn: getRequestTypes })
  const [off, setOff] = useState<string[] | null>(null)
  const disabled = off ?? data?.disabled ?? []
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState('')
  const mut = useMutation({
    mutationFn: () => setDisabledRequestTypes(disabled),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['request-types'] })
      setOff(d.disabled); setError(''); setSaved(true); setTimeout(() => setSaved(false), 2000)
    },
    onError: (e: any) => setError(e?.response?.data?.detail || 'Could not save'),
  })
  const toggle = (key: string) =>
    setOff(disabled.includes(key) ? disabled.filter((k) => k !== key) : [...disabled, key])

  if (!data) return <p className="text-sm text-gray-400">Loading…</p>
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 space-y-5">
      <p className="text-sm text-gray-500">
        Choose which requests employees can raise from the <strong>Requests</strong> page. Requests already raised are not affected.
      </p>
      {data.categories.map((c) => (
        <div key={c.key}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">{c.label}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.all_types.filter((t) => t.category === c.key).map((t) => (
              <label key={t.key} className="flex items-start gap-3 rounded-lg border border-gray-100 p-3 cursor-pointer hover:bg-gray-50">
                <input type="checkbox" className="mt-0.5 accent-[#EC5B2C]" checked={!disabled.includes(t.key)} onChange={() => toggle(t.key)} />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{t.label}</span>
                  <span className="block text-xs text-gray-500">{t.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      ))}
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex items-center gap-3">
        <button onClick={() => mut.mutate()} disabled={mut.isPending}
          className="px-4 py-2 text-sm font-medium text-white bg-[#EC5B2C] hover:bg-[#d44f23] rounded-lg disabled:opacity-60">
          {mut.isPending ? 'Saving…' : 'Save'}
        </button>
        {saved && <span className="text-sm text-green-600">Saved</span>}
      </div>
    </div>
  )
}
